import { useEffect, useRef, useCallback } from 'react';

export function useEditorWebSocket(onMessage: (data: any) => void, getToken: () => Promise<string>) {
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    // The server only ever talks after a successful auth handshake (its first
    // message is 'config_info'), so a message is our signal to reset the
    // backoff — otherwise a stuck/expired session would hammer the server
    // with a fresh connection every fixed interval forever.
    const BASE_RETRY_MS = 2000;
    const MAX_RETRY_MS = 30000;
    let retryDelay = BASE_RETRY_MS;

    async function connect() {
      if (closed) return;
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${location.host}/ws/editor`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        retryDelay = BASE_RETRY_MS;
        onMessageRef.current(JSON.parse(e.data));
      };
      ws.onclose = () => {
        if (closed) return;
        reconnectTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
      };
      ws.onopen = async () => {
        try {
          const token = await getTokenRef.current();
          ws.send(JSON.stringify({ action: 'auth', token }));
        } catch {
          ws.close();
        }
      };
    }

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, []);

  const pendingQueue = useRef<string[]>([]);

  const send = useCallback((data: unknown) => {
    const msg = JSON.stringify(data);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    } else {
      pendingQueue.current.push(msg);
    }
  }, []);

  return { send };
}
